import { useCan, useGo, useInvalidate, useList, useTranslate } from "@refinedev/core";
import { MultiselectInput } from "@components/molecules/MultiselectInput";
import { FaArrowRotateRight, FaPlus, FaTag, FaTrash } from "react-icons/fa6";
import { FaSearch, FaTimes } from "react-icons/fa";
import { Multiselect } from "@components/molecules/Multiselect";
import { SearchInput } from "@components/molecules/Search";
import { Controller, useForm } from "react-hook-form";
import { ContentCard } from "@components/organisms/ContentCard";
import { useState } from "react";
import { Article } from "types/articles";
import { DefaultTemplate } from "@components/templates/default";

/**
 * Props for the ContentList component.
 */
export interface ContentListProps {

}

/**
 * Main content listing component that handles post display, filtering, and search functionality.
 */
export function ContentList(props: ContentListProps) {
    const translate = useTranslate()
    const go = useGo()
    const invalidate = useInvalidate()
    const { data: canCreate } = useCan({
        resource: "articles",
        action: "create",
    });

    const [isMobileSearchActive, setIsMobileSearchActive] = useState(false);

    /**
     * Form for handling search and label filtering in the navbar
     */
    const navbarFilterForm = useForm<{
        search: string,
        labels: string[]
    }>({
        defaultValues: {
            labels: [],
            search: ''
        }
    })

    // Extract form control and watch for filter changes
    const {
        watch,
        control,
        setValue,
        formState: { errors },
    } = navbarFilterForm
    const filters = watch()
    // logger.debug(filters)

    const {
        data: dataPost,
    } = useList<Article>({
        resource: 'articles',
        queryOptions: {
            enabled: filters.search == '',
        },
        pagination: {
            pageSize: 50,
        },
        filters: [
            {
                field: 'tags',
                operator: '_contains' as 'contains',
                value: filters.labels,
            }
        ],
        meta: {
            fields: ['id', 'title', 'content', 'author_id', 'landing_image', 'tags', 'published_at', 'created_at', 'updated_at']
        }
    })

    const {
        data: dataPostSearch,
    } = useList<Article>({
        resource: 'articles',
        queryOptions: {
            enabled: filters.search != '',
        },
        pagination: {
            pageSize: 50,
        },
        filters: [
            {
                field: 'title',
                operator: '_contains' as 'contains',
                value: filters.search
            },
        ],
        meta: {
            fields: ['id', 'title', 'content', 'author_id', 'landing_image', 'tags', 'published_at', 'created_at', 'updated_at']
        }
    })

    const {
        data: dataFeatured,
    } = useList<Article>({
        resource: 'articles',
        queryOptions: {
            enabled: filters.search == '',
        },
        pagination: {
            pageSize: 3,
        },
        filters: [
            {
                field: 'tags',
                operator: '_contains' as 'contains',
                value: ['featured'],
            }
        ],
        meta: {
            fields: ['id', 'title', 'content', 'author_id', 'landing_image', 'tags', 'published_at', 'created_at', 'updated_at']
        }
    })

    const {
        data: dataSide,
    } = useList<Article>({
        resource: 'articles',
        queryOptions: {
            enabled: filters.search == '',
        },
        pagination: {
            pageSize: 3,
        },
        filters: [
            {
                field: 'tags',
                operator: '_contains' as 'contains',
                value: ['side'],
            }
        ],
        meta: {
            fields: ['id', 'title', 'content', 'author_id', 'landing_image', 'tags', 'published_at', 'created_at', 'updated_at']
        }
    })

    // Extract data from API responses
    const posts = dataPost?.data
    const postsSearchRaw = dataPostSearch?.data
    const postsFeatured = dataFeatured?.data
    const postsSide = dataSide?.data

    // Filter search results by selected labels
    const postsSearch = filters.labels.length > 0
        ? postsSearchRaw?.filter(p => filters.labels.every(l => p.tags?.includes(l)))
        : postsSearchRaw

    // logger.debug(posts)

    // Available label options for filtering
    const optionLabels = ['pro', 'health', 'other', 'yoga', 'featured']

    // if some options need to be obtained from API
    // const { selectProps: categorySelectProps } = useSelect<ICategory>({
    //     resource: "blogger/blogs/",
    //     optionLabel: "title",
    //     optionValue: "id",
    //     defaultValue: getDefaultFilter("category.id", filters, "in"),
    //   });

    // const { data: canShowContent } = useCan({
    //     resource: "content",
    //     action: "show",
    //     // params: { foo: "optional-params" },
    // });
    // logger.debug(canShowContent)

    /**
     * Function to invalidate and refresh post data
     */
    const invalidatePosts = async () => {
        await invalidate({
            resource: 'articles',
            invalidates: ['list']
        })
    }

    // logger.debug(posts)

    return <DefaultTemplate
        title={translate("page.content.title", "Contenido")}
        left={<div className="md:hidden">
            {isMobileSearchActive ? (
                <section className="flex w-full">
                    <Controller
                        control={control}
                        name="search"
                        rules={{
                            required: false,
                        }}
                        render={({ field }) => (
                            <SearchInput
                                className="grow input-bordered join-item absolute left-0 ml-4"
                                cleanIcon={FaTrash}
                                {...field}
                                autoFocus
                            />
                        )}
                    />
                    <button
                        className="btn btn-ghost join-item"
                        onClick={() => setIsMobileSearchActive(false)}
                    >
                        <FaTimes />
                    </button>
                </section>
            ) : (
                <section className="flex-1 join">
                    <button
                        className="btn join-item"
                        onClick={() => setIsMobileSearchActive(true)}
                    >
                        <FaSearch />
                    </button>
                    <Controller
                        control={control}
                        name="labels"
                        rules={{
                            required: true,
                        }}
                        render={({ field }) => (
                            <Multiselect
                                className="dropdown-left"
                                optionLabels={optionLabels}
                                {...field}
                            >
                                <button className="btn btn-primary flex-nowrap">
                                    <FaTag />
                                </button>
                            </Multiselect>
                        )}
                    />
                    <button className="btn btn-secondary join-item" onClick={invalidatePosts}>
                        <FaArrowRotateRight />
                    </button>
                </section>)}
        </div>}
        right={canCreate?.can ? <button className="btn btn-primary gap-2" onClick={() => go({ to: '/content/create' })}>
            <FaPlus />
            <span className="hidden md:inline">{translate("articles.buttons.create", "Nuevo Artículo")}</span>
        </button> : null}
        subnavbar={<nav className="w-full hidden md:join">
            <Controller
                control={control}
                name="search"
                rules={{
                    required: true,
                }}
                render={({ field }) => (
                    <SearchInput className="grow input-bordered join-item" {...field} />
                )}
            />
            <Controller
                control={control}
                name="labels"
                rules={{
                    required: true,
                }}
                render={({ field }) => (
                    <MultiselectInput
                        label={<span className="flex gap-1 mr-1 items-center"><FaTag /></span>}
                        className="input-bordered join-item w-1/3"
                        optionLabels={optionLabels}
                        {...field}
                    />)}
            />
            <button className="btn btn-secondary join-item" onClick={invalidatePosts}>
                <FaArrowRotateRight />
            </button>
        </nav>}>
        {/* Main content area */}
        {filters.search == '' ? <>
            {/* Featured posts section */}
            {postsFeatured && <FeaturedPosts posts={postsFeatured} />}

            {/* Main content layout with posts and sidebar */}
            <section className="mt-12 flex">
                {/* Primary content area with responsive grid */}
                <article className="flex-wrap mx-2 flex gap-y-4 grow">
                    {(!posts || posts.length === 0) ? (
                        <div className="w-full flex flex-col items-center justify-center py-12 px-4 text-center bg-base-200/30 rounded-3xl border-2 border-dashed border-base-300">
                            <div className="text-5xl mb-4 opacity-50">📄</div>
                            <h3 className="text-xl font-bold">{translate("articles.empty.title", "No hay artículos")}</h3>
                            <p className="text-base-content/60 max-w-md mx-auto mt-2">
                                {translate("articles.empty.description", "No se encontraron artículos en esta categoría. Puedes intentar cambiar los filtros o crear uno nuevo.")}
                            </p>
                            {canCreate?.can && <button className="btn btn-outline btn-primary mt-6" onClick={() => go({ to: '/content/create' })}>
                                {translate("articles.buttons.create_first", "Crear mi primer artículo")}
                            </button>}
                        </div>
                    ) : (
                        posts.map((post, i) => post && <ContentCard
                            key={post.id}
                            variant="responsive"
                            post={post}
                            className={'px-2 w-full md:w-1/2 lg:w-1/3'} />)
                    )}
                </article>

                {/* Sidebar with additional content */}
                {postsSide && postsSide.length > 0 && (
                    <aside className="flex-wrap mx-2 hidden xl:flex flex-col gap-4 w-1/4">
                        <header className="prose">
                            <h2 className="">Featured Content</h2>
                        </header>
                        {postsSide.map(post => post && <ContentCard key={post.id} variant="short" post={post} />)}
                    </aside>
                )}
            </section>
        </> : <>
            {/* Search results layout */}
            <section className="mt-12 flex flex-col gap-4">
                {(!postsSearch || postsSearch.length === 0) ? (
                    <div className="w-full flex flex-col items-center justify-center py-12 px-4 text-center bg-base-200/30 rounded-3xl border-2 border-dashed border-base-300">
                        <div className="text-5xl mb-4 opacity-50">🔍</div>
                        <h3 className="text-xl font-bold">{translate("articles.search.empty.title", "No se encontraron resultados")}</h3>
                        <p className="text-base-content/60 max-w-md mx-auto mt-2">
                            {translate("articles.search.empty.description", "No pudimos encontrar nada que coincida con lo que buscas. Intenta con otros términos.")}
                        </p>
                        <button className="btn btn-ghost mt-4" onClick={() => setValue('search', '')}>
                            {translate("common.buttons.clear_filters", "Limpiar filtros")}
                        </button>
                    </div>
                ) : (
                    postsSearch?.map(post => post && <ContentCard key={post.id} variant="responsive" post={post} className="" />)
                )}
            </section>
        </>
        }
    </DefaultTemplate>
}

/**
 * Featured posts component that displays posts in a hero layout
 * @param posts - Array of featured posts to display
 */
const FeaturedPosts = ({ posts }: { posts: Article[] }) => <section className="flex flex-col lg:flex-row px-6 my-4 gap-2 lg:max-h-[40vh]">
    {posts?.[0] && <ContentCard post={posts[0]} variant="hero" className={`lg:h-full w-full ${posts.length === 1 ? 'lg:w-full' : 'lg:w-1/2'}`} />}
    {posts.length > 1 && <article className="flex flex-col w-full lg:w-1/2 gap-2">
        {posts?.[1] && <ContentCard post={posts![1]} variant="hero" className={`w-full ${posts.length === 2 ? 'lg:h-full' : 'lg:h-1/2'}`} />}
        {posts?.[2] && <ContentCard post={posts![2]} variant="hero" className="w-full lg:h-1/2" />}
    </article>}
</section>
